import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdAdd, MdBusiness, MdCheckCircle, MdDeleteForever, MdEdit, MdEmail, MdGroups, MdHourglassTop, MdPhone, MdSearch, MdTune, MdVisibility } from 'react-icons/md';
import { getData, putData, deleteData } from '../../Webservice/Webservice';
import CustomerForm from './CustomerForm';
import { CustomerAvatar, FONT, TypeChip, authToken, displayId, fmtDate, inr } from './customerShared';

const PAGE_SIZE = 10;
const EMPTY_FILTERS = { gender: '', provider: '', businessType: '', isDeleted: '' };
const iconBtn = (color, bg) => ({ color, background: bg, borderRadius: '8px', '&:hover': { background: bg, filter: 'brightness(.95)' } });
const switchSx = (on, off) => ({
  '& .MuiSwitch-switchBase.Mui-checked': { color: on },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: on },
  '& .MuiSwitch-track': { bgcolor: off },
  '& .MuiSwitch-switchBase': { color: off },
});

function Customermanagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState('list'); // list | form
  const [editing, setEditing] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, business: 0, verified: 0 });
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [verified, setVerified] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [drawer, setDrawer] = useState(false);
  const [temp, setTemp] = useState(EMPTY_FILTERS);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const params = { page: meta.page, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (status) params.status = status;
      if (type) params.customerType = type;
      if (verified) params.isVerified = verified;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await getData('customer/list', params, authToken(), false);
      setCustomers(Array.isArray(res?.customer) ? res.customer : []);
      if (res?.stats) setStats(res.stats);
      const m = res?.meta || {};
      setMeta({ page: m.page || 1, totalPages: m.totalPages || 1, total: m.total || 0 });
      setError('');
    } catch (e) {
      setCustomers([]);
      setError('Could not load customers. Please try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, search, status, type, verified, filters]);

  useEffect(() => { if (view === 'list') load(); }, [view, load]);

  // wait for the user to stop typing before searching
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setMeta((p) => ({ ...p, page: 1 })); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // arriving from a customer's page with "Edit" pressed
  useEffect(() => {
    const id = location.state?.editId;
    if (!id) return;
    navigate(location.pathname, { replace: true, state: null });
    getData(`customer/${id}`, null, authToken(), false)
      .then((c) => { if (c?._id) { setEditing(c); setView('form'); } })
      .catch(() => setError('Could not open that customer for editing.'));
  }, [location.state, location.pathname, navigate]);

  const reset = (setter) => (v) => { setter(v); setMeta((p) => ({ ...p, page: 1 })); };
  const hasFilters = !!(search || status || type || verified || Object.values(filters).some(Boolean));
  const clearAll = () => {
    setSearchInput(''); setSearch(''); setStatus(''); setType(''); setVerified('');
    setFilters(EMPTY_FILTERS); setTemp(EMPTY_FILTERS); setMeta((p) => ({ ...p, page: 1 }));
  };

  const toggle = async (c, patch) => {
    try {
      await putData(`customer/statusUpdate/${c._id}`, patch, authToken());
      setCustomers((list) => list.map((x) => (x._id === c._id ? { ...x, ...patch } : x)));
      load(true);
    } catch (e) {
      setError('Could not update the customer.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteData(`customer/delete/${deleteTarget._id}`, authToken());
      setDeleteTarget(null);
      if (customers.length === 1 && meta.page > 1) setMeta((p) => ({ ...p, page: p.page - 1 }));
      else await load(true);
    } catch (e) {
      setError('Could not delete the customer.');
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  if (view === 'form') {
    return <CustomerForm customer={editing} onBack={() => { setView('list'); setEditing(null); }} onSaved={() => { setView('list'); setEditing(null); }} />;
  }

  const kpis = [
    { label: 'All customers', value: stats.total, icon: MdGroups, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', on: () => { setStatus(''); setType(''); }, active: !status && !type },
    { label: 'Active', value: stats.active, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', on: () => { setStatus('active'); setType(''); }, active: status === 'active' },
    { label: 'Pending verification', value: stats.pending, icon: MdHourglassTop, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', on: () => { setStatus('verificationPending'); setType(''); }, active: status === 'verificationPending' },
    { label: 'Business accounts', value: stats.business, icon: MdBusiness, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', on: () => { setType('business'); setStatus(''); }, active: type === 'business' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Customers</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>See who is buying, manage their accounts and open their full history.</Typography>
          </Box>
          <Button variant="contained" startIcon={<MdAdd />} onClick={() => { setEditing(null); setView('form'); }} sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}>Add Customer</Button>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
          {kpis.map((k) => (
            <Paper key={k.label} elevation={0} onClick={() => { k.on(); setMeta((p) => ({ ...p, page: 1 })); }} sx={{ p: 2.25, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: k.active ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}>
              <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.4, display: 'flex' }}><k.icon size={24} /></Box>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
                <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search name, email, mobile or customer ID" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1, minWidth: 240 }} InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }} />
          <TextField select size="small" label="Status" value={status} onChange={(e) => reset(setStatus)(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">All</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem><MenuItem value="verificationPending">Pending</MenuItem>
          </TextField>
          <TextField select size="small" label="Type" value={type} onChange={(e) => reset(setType)(e.target.value)} sx={{ minWidth: 120 }}>
            <MenuItem value="">All</MenuItem><MenuItem value="customer">Customer</MenuItem><MenuItem value="business">Business</MenuItem>
          </TextField>
          <TextField select size="small" label="Verified" value={verified} onChange={(e) => reset(setVerified)(e.target.value)} sx={{ minWidth: 120 }}>
            <MenuItem value="">All</MenuItem><MenuItem value="true">Verified</MenuItem><MenuItem value="false">Unverified</MenuItem>
          </TextField>
          <Button startIcon={<MdTune />} onClick={() => { setTemp(filters); setDrawer(true); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', border: '1px solid #e2e8f0', borderRadius: '10px', px: 2 }}>More filters</Button>
          {hasFilters && <Button onClick={clearAll} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>}
        </Paper>

        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.05)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.6, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' } }}>
                <TableCell>Customer</TableCell><TableCell>Contact</TableCell><TableCell>Type</TableCell><TableCell align="right">Orders</TableCell><TableCell align="center">Active</TableCell><TableCell align="center">Verified</TableCell><TableCell>Joined</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 8 }).map((__, j) => <TableCell key={j}><Skeleton height={34} /></TableCell>)}</TableRow>)
              ) : customers.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}><Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>{hasFilters ? 'No customers match your filters.' : 'No customers yet.'}</Typography></TableCell></TableRow>
              ) : customers.map((c) => (
                <TableRow key={c._id} hover onClick={() => navigate(`/customermanagement/${c._id}`)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.4, fontFamily: FONT } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} badgeContent={<Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff', bgcolor: c.status === 'active' ? '#10b981' : c.status === 'inactive' ? '#ef4444' : '#f59e0b' }} />}>
                        <CustomerAvatar customer={c} size={38} />
                      </Badge>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, color: '#0f172a' }}>{c.name || 'Unnamed'}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{displayId(c)}{c.businessName ? ` · ${c.businessName}` : ''}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><MdEmail size={13} color="#94a3b8" /><Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#334155' }}>{c.email || '—'}</Typography></Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><MdPhone size={13} color="#94a3b8" /><Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{c.mobile || '—'}</Typography></Box>
                  </TableCell>
                  <TableCell><TypeChip type={c.customerType} /></TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{c.totalOrders || 0}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{inr(c.totalSpent)}</Typography>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={c.status === 'active' ? 'Deactivate' : 'Activate'} arrow><span><Switch size="small" checked={c.status === 'active'} onChange={() => toggle(c, { status: c.status === 'active' ? 'inactive' : 'active' })} sx={switchSx('#10b981', '#ef4444')} /></span></Tooltip>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={c.isVerified ? 'Mark unverified' : 'Mark verified'} arrow><span><Switch size="small" checked={!!c.isVerified} onChange={() => toggle(c, { isVerified: !c.isVerified })} sx={switchSx('#3b82f6', '#9ca3af')} /></span></Tooltip>
                  </TableCell>
                  <TableCell><Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#475569' }}>{fmtDate(c.createdAt)}</Typography></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View" arrow><IconButton size="small" onClick={() => navigate(`/customermanagement/${c._id}`)} sx={{ ...iconBtn('#4f46e5', 'rgba(79,70,229,.1)'), mr: 0.75 }}><MdVisibility size={16} /></IconButton></Tooltip>
                    <Tooltip title="Edit" arrow><IconButton size="small" onClick={() => { setEditing(c); setView('form'); }} sx={{ ...iconBtn('#ca8a04', '#fefce8'), mr: 0.75 }}><MdEdit size={16} /></IconButton></Tooltip>
                    <Tooltip title="Delete" arrow><IconButton size="small" onClick={() => setDeleteTarget(c)} sx={iconBtn('#dc2626', 'rgba(220,38,38,.1)')}><MdDeleteForever size={18} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {meta.total > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={meta.totalPages} page={meta.page} shape="rounded" showFirstButton showLastButton onChange={(_, p) => setMeta((m) => ({ ...m, page: p }))} sx={{ '& .MuiPagination-ul': { flexWrap: 'nowrap' }, '& .MuiPaginationItem-root': { fontFamily: FONT, fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } } } }} />
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} customers</Typography>
          </>
        )}
      </Box>

      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)}>
        <Box sx={{ width: 320, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18 }}>More filters</Typography>
          <TextField select fullWidth size="small" label="Gender" value={temp.gender} onChange={(e) => setTemp((p) => ({ ...p, gender: e.target.value }))}>
            <MenuItem value="">Any</MenuItem><MenuItem value="male">Male</MenuItem><MenuItem value="female">Female</MenuItem><MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField select fullWidth size="small" label="Signed up with" value={temp.provider} onChange={(e) => setTemp((p) => ({ ...p, provider: e.target.value }))}>
            <MenuItem value="">Any</MenuItem><MenuItem value="email">Email</MenuItem><MenuItem value="mobile">Mobile</MenuItem><MenuItem value="google">Google</MenuItem><MenuItem value="facebook">Facebook</MenuItem><MenuItem value="admin">Added by admin</MenuItem>
          </TextField>
          <TextField select fullWidth size="small" label="Business type" value={temp.businessType} onChange={(e) => setTemp((p) => ({ ...p, businessType: e.target.value }))}>
            <MenuItem value="">Any</MenuItem><MenuItem value="proprietorship">Proprietorship</MenuItem><MenuItem value="partnership">Partnership</MenuItem><MenuItem value="llp">LLP</MenuItem><MenuItem value="private_limited">Private limited</MenuItem><MenuItem value="public_limited">Public limited</MenuItem>
          </TextField>
          <TextField select fullWidth size="small" label="Deleted accounts" value={temp.isDeleted} onChange={(e) => setTemp((p) => ({ ...p, isDeleted: e.target.value }))}>
            <MenuItem value="">Hide</MenuItem><MenuItem value="true">Show only deleted</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button fullWidth onClick={() => { setTemp(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setDrawer(false); setMeta((p) => ({ ...p, page: 1 })); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', border: '1px solid #e2e8f0' }}>Reset</Button>
            <Button fullWidth variant="contained" onClick={() => { setFilters(temp); setDrawer(false); setMeta((p) => ({ ...p, page: 1 })); }} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', '&:hover': { background: 'rgb(255 190 0)' } }}>Apply</Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={!!deleteTarget} onClose={() => !busy && setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Delete customer?</DialogTitle>
        <DialogContent><Typography sx={{ fontFamily: FONT }}><b>{deleteTarget?.name || 'This customer'}</b> will be removed and archived. They will no longer be able to sign in, and this can't be undone from the panel.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{busy ? 'Deleting…' : 'Yes, delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Customermanagement;
